/**
 * UserEditFormComponent
 *
 * A comprehensive form component for editing user details and associated questions
 * in the FeedMe Sales Panel application. This component provides a rich interface
 * for managing both customer information and their questionnaire.
 *
 * Features:
 * - Modal and inline display modes
 * - Real-time form validation
 * - Visual preview of changes before submission
 * - Parallel question updates with error handling
 * - Loading states and user feedback
 * - Responsive design with mobile support
 * - Modern UI with smooth transitions
 *
 * @example
 * ```html
 * <app-user-edit-form
 *   [user]="selectedUser"
 *   [inlineMode]="false"
 *   (save)="handleSave($event)"
 *   (cancel)="handleCancel()"
 * >
 * </app-user-edit-form>
 * ```
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import {
  Question,
  QuestionType,
  RedirectQuestion,
  QUESTION_TYPE_LABELS,
  CustomQuestion,
} from '../../../models/question.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../../../environments/environment';

/**
 * Interface representing a saved form with customer details and questions
 */
interface SavedForm {
  /** Unique identifier for the form */
  id: string;
  /** Customer's personal information */
  customerDetails: {
    /** Customer's business or personal name */
    name: string;
    /** Customer's email address */
    email: string;
    /** Customer's password (not used in edit mode) */
    password: string;
  };
  /** Serialized questions data */
  questions: string;
  /** Timestamp of form creation */
  createdAt: string;
}

/**
 * Interface representing the edited user data to be saved
 */
interface EditedUser {
  /** User's unique identifier */
  id: string;
  /** Updated customer details */
  customerDetails: {
    /** Updated name */
    name: string;
    /** Updated email */
    email: string;
  };
  /** Array of modified questions */
  questions: Question[];
}

/**
 * Interface for question update requests to the API
 */
interface QuestionUpdate {
  /** Question's unique identifier */
  QueryID: string;
  /** Updated question text */
  Body: string;
  /** Redirect URL for type 21 questions */
  Link?: string;
  /** Question type identifier */
  TypeID?: string;
  /** Question status */
  Active?: boolean;
}

@Component({
  selector: 'app-user-edit-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    @if (!inlineMode) {
      <!-- Backdrop with smooth transition -->
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300 ease-in-out z-40"></div>

      <!-- Modal Container -->
      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 scale-100 opacity-100">
            <!-- Close Button -->
            <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                (click)="onCancel()"
                class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 transition-colors duration-200"
              >
                <span class="sr-only">Chiudi</span>
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <ng-container [ngTemplateOutlet]="formContent"></ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Inline version -->
      <div class="p-6">
        <ng-container [ngTemplateOutlet]="formContent"></ng-container>
      </div>
    }

    <ng-template #formContent>
      <!-- Header with icon -->
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-secondary bg-opacity-10 p-2 rounded-full">
          <svg class="h-6 w-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold leading-6 text-gray-900">Modifica {{ user.customerDetails.name }}</h3>
      </div>

      <form [formGroup]="editForm" (ngSubmit)="onSubmit()" class="mt-6">
        <div class="md:flex md:gap-6">
          <!-- Customer Details Section -->
          <div formGroupName="customerDetails" class="md:w-1/3 space-y-4 p-4 bg-gray-50 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-4">Dettagli Cliente</h4>

            <div class="space-y-2">
              <label for="name" class="block text-sm font-medium text-gray-700">Nome</label>
              <div class="relative">
                <input
                  type="text"
                  id="name"
                  formControlName="name"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm transition-all duration-200"
                  [class.border-red-300]="editForm.get('customerDetails.name')?.invalid && editForm.get('customerDetails.name')?.touched"
                />
                <div *ngIf="editForm.get('customerDetails.name')?.invalid && editForm.get('customerDetails.name')?.touched"
                     class="text-red-500 text-xs mt-1">
                  Campo obbligatorio
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
              <div class="relative">
                <input
                  type="email"
                  id="email"
                  formControlName="email"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm transition-all duration-200"
                  [class.border-red-300]="editForm.get('customerDetails.email')?.invalid && editForm.get('customerDetails.email')?.touched"
                />
                <div *ngIf="editForm.get('customerDetails.email')?.invalid && editForm.get('customerDetails.email')?.touched"
                     class="text-red-500 text-xs mt-1">
                  Inserire un indirizzo email valido
                </div>
              </div>
            </div>
          </div>

          <!-- Questions Section -->
          <div class="md:w-2/3 space-y-4 mt-6 md:mt-0">
            <div class="flex items-center justify-between">
              <h4 class="font-medium text-gray-900">Domande</h4>
              <span class="text-sm text-gray-500">{{ questions.length }} domande</span>
            </div>

            <div formArrayName="questions" class="space-y-4 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2">
              @for (question of questions.controls; track $index) {
                <div [formGroupName]="$index" class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div class="flex items-center justify-between mb-2">
                    <label [for]="'question-' + $index" class="block text-sm font-medium text-gray-700">
                      Domanda {{ $index + 1 }}
                    </label>
                    <span class="text-xs px-2 py-1 rounded-full"
                          [class.bg-blue-100.text-blue-700]="question.get('type')?.value === '1'"
                          [class.bg-green-100.text-green-700]="question.get('type')?.value === '2'"
                          [class.bg-green-100.text-green-700]="question.get('type')?.value === '3'"
                          [class.bg-green-100.text-green-700]="question.get('type')?.value === '4'"
                          [class.bg-purple-100.text-purple-700]="question.get('type')?.value === '21'">
                      {{ getQuestionTypeLabel(question.get('type')?.value) }}
                    </span>
                  </div>

                  <input
                    [id]="'question-' + $index"
                    type="text"
                    formControlName="title"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm transition-all duration-200"
                    [class.border-red-300]="question.get('title')?.invalid && question.get('title')?.touched"
                  />

                  @if (question.get('type')?.value === '21') {
                    <input
                      [id]="'question-url-' + $index"
                      type="url"
                      formControlName="url"
                      placeholder="URL di reindirizzamento"
                      class="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm transition-all duration-200"
                      [class.border-red-300]="question.get('url')?.invalid && question.get('url')?.touched"
                    />
                    <div *ngIf="question.get('url')?.invalid && question.get('url')?.touched"
                         class="text-red-500 text-xs mt-1">
                      Inserire un URL valido
                    </div>
                  }
                  @if (question.get('type')?.value === '4') {
                    <input
                      [id]="'question-responseType-' + $index"
                      type="text"
                      formControlName="responseType"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm transition-all duration-200"
                      [class.border-red-300]="question.get('responseType')?.invalid && question.get('responseType')?.touched"
                    />
                  }
                  <button
                    type="button"
                    (click)="deleteSingleQuestion(question.get('queryId')?.value)"
                    class="inline-flex w-full items-center justify-center rounded-md bg-secondary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-secondary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >Elimina domanda</button>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Form Actions with loading state -->
        <div class="mt-6 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
          <button
            type="submit"
            [disabled]="!editForm.valid || isSaving"
            class="inline-flex w-full items-center justify-center rounded-md bg-secondary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-secondary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            @if (isSaving) {
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Salvataggio...
            } @else {
              Salva Modifiche
            }
          </button>
          <button
            type="button"
            (click)="onCancel()"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-200"
          >
            Annulla
          </button>
        </div>
      </form>
    </ng-template>
  `
})
export class UserEditFormComponent {
  /** The user data to edit */
  @Input() user!: SavedForm;

  /** Whether to display the form inline or as a modal */
  @Input() inlineMode = false;

  /** Event emitter for successful save operations */
  @Output() save = new EventEmitter<EditedUser>();

  /** Event emitter for cancellation */
  @Output() cancel = new EventEmitter<void>();

  /** Form group for managing the edit form */
  editForm!: FormGroup;

  /** Flag indicating whether a save operation is in progress */
  isSaving = false;

  /** API endpoint for updating questions */
  private readonly API_URL = environment.apiUrl + '/dashboard/UpdateQuestion';
  /** API endpoint for updating user details */
  private readonly USER_API_URL = environment.apiUrl+'/users';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.initializeForm();
  }

  /**
   * Initializes the form group with customer details and questions
   */
  private initializeForm(): void {
    this.editForm = this.fb.group({
      customerDetails: this.fb.group({
        name: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]]
      }),
      questions: this.fb.array([])
    });
  }

  /**
   * Lifecycle hook that initializes the component
   * Sets up the form with user data and questions
   */
  ngOnInit() {
    // Initialize form with user data
    this.editForm.patchValue({
      customerDetails: {
        name: this.user.customerDetails.name,
        email: this.user.customerDetails.email
      }
    });

    // Initialize questions
    const questions = this.parsedQuestions;
    const questionControls = questions.map(question => {
      const baseGroup = {
        queryId: [question.queryId],
        title: [question.title, Validators.required],
        type: [question. type]
      };

      if (question.type === '21') {
        const redirectQuestion = question as RedirectQuestion;
        const url = redirectQuestion.url || 'URL non trovato';

        return this.fb.group({
          ...baseGroup,
          url: [url, [Validators.required, Validators.pattern('https?://.+')]]
        });
      }

      if (question.type === '4') {
        const customQuestion = question as CustomQuestion;
        const responseType = customQuestion.responseType || 'Response type not found';

        return this.fb.group({
          ...baseGroup,
          responseType: [question.responseType || ''] // ← aggiunto
        });
      }

      return this.fb.group(baseGroup);
    });

    const questionsArray = this.editForm.get('questions') as FormArray;
    questionControls.forEach(control => questionsArray.push(control));
  }

  /**
   * Gets the questions form array
   * @returns FormArray containing question controls
   */
  get questions() {
    return this.editForm.get('questions') as FormArray;
  }

  /**
   * Parses the questions string into an array of Question objects
   * @returns Array of parsed questions
   */
  get parsedQuestions(): Question[] {
    try {
      if (typeof this.user.questions === 'string') {
        const parsed = JSON.parse(this.user.questions);
        return parsed;
      }
      return this.user.questions as unknown as Question[];
    } catch (e) {
      return [];
    }
  }

  /**
   * Handles form submission
   * Validates changes, shows preview, and updates questions in parallel
   */
  async onSubmit() {
    if (this.editForm.valid) {
      const formValue = this.editForm.value;
      const originalQuestions = this.parsedQuestions;
      const modifiedQuestions = formValue.questions;

      // Create HTML content for changes preview
      let changesHtml = '<div class="text-left">';
      const questionUpdates: { [key: string]: QuestionUpdate } = {};

      // Add customer details changes preview
      const customerDetailsChanged =
        this.user.customerDetails.name !== formValue.customerDetails.name ||
        this.user.customerDetails.email !== formValue.customerDetails.email;

      if (customerDetailsChanged) {
        changesHtml += `
          <div class="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-100 shadow-sm">
            <div class="flex items-center gap-2 mb-3">
              <span class="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 text-secondary">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              <h3 class="text-lg font-semibold text-gray-900">Dettagli Cliente</h3>
            </div>
            <div class="space-y-4">
              ${this.user.customerDetails.name !== formValue.customerDetails.name ? `
                <div>
                  <p class="text-sm font-medium text-gray-500 mb-2">Nome:</p>
                  <div class="space-y-2">
                    <div class="flex items-center gap-2">
                      <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                      </svg>
                      <p class="text-base text-red-600 line-through font-medium">${this.user.customerDetails.name}</p>
                    </div>
                    <div class="flex items-center gap-2">
                      <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <p class="text-base text-green-600 font-medium">${formValue.customerDetails.name}</p>
                    </div>
                  </div>
                </div>
              ` : ''}
              ${this.user.customerDetails.email !== formValue.customerDetails.email ? `
                <div>
                  <p class="text-sm font-medium text-gray-500 mb-2">Email:</p>
                  <div class="space-y-2">
                    <div class="flex items-center gap-2">
                      <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                      </svg>
                      <p class="text-base text-red-600 line-through font-medium">${this.user.customerDetails.email}</p>
                    </div>
                    <div class="flex items-center gap-2">
                      <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <p class="text-base text-green-600 font-medium">${formValue.customerDetails.email}</p>
                    </div>
                  </div>
                </div>
              ` : ''}
            </div>
          </div>
        `;
      }

      modifiedQuestions.forEach((newQ: Question, index: number) => {
        const oldQ = originalQuestions[index];
        const hasChanges = oldQ.title !== newQ.title ||
          (oldQ.type === '21' &&
            (oldQ as RedirectQuestion).url !== (newQ as RedirectQuestion).url) || (oldQ.type === '4' &&
          (oldQ as CustomQuestion).responseType !== (newQ as CustomQuestion).responseType);

        if (oldQ && hasChanges) {
          questionUpdates[oldQ.queryId] = {
            QueryID: oldQ.queryId,
            Body: newQ.title,
            ...(newQ.type === '21' && { Link: (newQ as RedirectQuestion).url }),
            ...(newQ.type === '4' && { ResponseType: (newQ as CustomQuestion).responseType }),
            TypeID: newQ.type,
            Active: true
          };

          changesHtml += this.generateChangePreviewHtml(oldQ, newQ, index);
        }
      });
      changesHtml += '</div>';

      // Only show preview if there are actual changes
      if (customerDetailsChanged || Object.keys(questionUpdates).length > 0) {
        const confirmed = await this.showChangePreview(changesHtml);
        if (confirmed) {
          await this.saveChanges(questionUpdates, formValue);
        }
      } else {
        await Swal.fire({
          title: 'Nessuna modifica',
          text: 'Non sono state apportate modifiche ai dati',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      }
    }
  }

  /**
   * Generates HTML for the change preview dialog
   * @param oldQ Original question
   * @param newQ Modified question
   * @param index Question index
   * @returns HTML string for the preview
   */
  private generateChangePreviewHtml(oldQ: Question, newQ: Question, index: number): string {
    return `
      <div class="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-100 shadow-sm">
        <div class="flex items-center gap-2 mb-3">
          <span class="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 text-secondary font-semibold">
            ${index + 1}
          </span>
          <h3 class="text-lg font-semibold text-gray-900">Domanda ${index + 1}</h3>
        </div>
        <div class="space-y-4">
          <div>
            <p class="text-sm font-medium text-gray-500 mb-2">Testo della domanda:</p>
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                </svg>
                <p class="text-base text-red-600 line-through font-medium">${oldQ.title}</p>
              </div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                <p class="text-base text-green-600 font-medium">${newQ.title}</p>
              </div>
            </div>
          </div>
          ${oldQ.type === '21' ? this.generateRedirectUrlPreviewHtml(oldQ as RedirectQuestion, newQ as RedirectQuestion) : ''}
          ${oldQ.type === '4' ? this.generateCustomQueryPreviewHtml(oldQ as CustomQuestion, newQ as CustomQuestion) : ''}
        </div>
      </div>
    `;
  }

  /**
   * Generates HTML for the redirect URL preview
   * @param oldQ Original redirect question
   * @param newQ Modified redirect question
   * @returns HTML string for the URL preview
   */
  private generateRedirectUrlPreviewHtml(oldQ: RedirectQuestion, newQ: RedirectQuestion): string {
    return `
      <div>
        <p class="text-sm font-medium text-gray-500 mb-2">URL di reindirizzamento:</p>
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
            </svg>
            <p class="text-base text-red-600 line-through font-medium">${oldQ.url || 'https://feedmevps.com/feedback'}</p>
          </div>
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <p class="text-base text-green-600 font-medium">${newQ.url}</p>
          </div>
        </div>
      </div>
    `;
  }

  private generateCustomQueryPreviewHtml(oldQ: CustomQuestion, newQ: CustomQuestion): string {
    return `
      <div>
        <p class="text-sm font-medium text-gray-500 mb-2">URL di reindirizzamento:</p>
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
            </svg>
            <p class="text-base text-red-600 line-through font-medium">${oldQ.responseType || 'https://feedmevps.com/feedback'}</p>
          </div>
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <p class="text-base text-green-600 font-medium">${newQ.responseType}</p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Shows the change preview dialog
   * @param changesHtml HTML content for the preview
   * @returns Promise resolving to true if confirmed
   */
  private async showChangePreview(changesHtml: string): Promise<boolean> {
    const result = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-900 mb-2">Anteprima Modifiche</h2>',
      html: changesHtml,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Conferma e Salva',
      cancelButtonText: 'Annulla',
      customClass: {
        container: 'swal2-container',
        popup: 'rounded-xl shadow-xl max-w-3xl',
        htmlContainer: 'text-left',
        title: 'border-b border-gray-200 pb-4',
        confirmButton: 'bg-secondary hover:bg-secondary-dark text-white px-6 py-2.5 rounded-lg font-medium transition-colors',
        cancelButton: 'bg-white hover:bg-gray-50 text-gray-900 px-6 py-2.5 rounded-lg font-medium transition-colors border border-gray-300'
      },
      buttonsStyling: false
    });

    return result.isConfirmed;
  }

  private async showConfirm(changesHtml: string): Promise<boolean> {
    const result = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-900 mb-2">Cancellazione domanda</h2>',
      html: '',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Conferma',
      cancelButtonText: 'Annulla',
      customClass: {
        container: 'swal2-container',
        popup: 'rounded-xl shadow-xl max-w-3xl',
        htmlContainer: 'text-left',
        title: 'border-b border-gray-200 pb-4',
        confirmButton: 'bg-secondary hover:bg-secondary-dark text-white px-6 py-2.5 rounded-lg font-medium transition-colors',
        cancelButton: 'bg-white hover:bg-gray-50 text-gray-900 px-6 py-2.5 rounded-lg font-medium transition-colors border border-gray-300'
      },
      buttonsStyling: false
    });

    return result.isConfirmed;
  }

  /**
   * Saves the changes to the backend
   * @param questionUpdates Object containing question updates
   * @param formValue Current form values
   */
  private async saveChanges(questionUpdates: { [key: string]: QuestionUpdate }, formValue: any) {
    this.isSaving = true;
    this.editForm.disable();

    try {
      // Update user details first
      const userUpdate = {
        firstName: formValue.customerDetails.name,
        lastName: formValue.customerDetails.name,
        email: formValue.customerDetails.email
      };

      await firstValueFrom(
        this.http.put(`${this.USER_API_URL}/${this.user.id}`, userUpdate).pipe(
          catchError((error: HttpErrorResponse) => {
            console.error('Error updating user details:', error);
            throw new Error(`Failed to update user details: ${error.error?.message || error.message}`);
          })
        )
      );

      // Update questions in parallel
      const updatePromises = Object.entries(questionUpdates).map(([queryId, update]) =>
        firstValueFrom(
          this.http.post(`${this.API_URL}/${queryId}`, JSON.stringify(update), {
            headers: {
              'Content-Type': 'application/json'
            }
          }).pipe(
            catchError((error: HttpErrorResponse) => {
              console.error(`Error updating question ${queryId}:`, error);
              throw new Error(`Failed to update question ${queryId}: ${error.error?.error || error.message}`);
            })
          )
        )
      );

      await Promise.all(updatePromises);

      const editedUser: EditedUser = {
        id: this.user.id,
        customerDetails: formValue.customerDetails,
        questions: formValue.questions
      };

      this.save.emit(editedUser);

      await this.showSuccessMessage();
      this.onCancel();

    } catch (error) {
      console.error('Error saving changes:', error);
      this.editForm.enable();
      await this.showErrorMessage(error);
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Shows a success message after saving changes
   */
  private async showSuccessMessage() {
    await Swal.fire({
      title: 'Modifiche salvate',
      text: 'Le modifiche sono state salvate con successo',
      icon: 'success',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      timer: 1500,
      timerProgressBar: true
    });
  }

  /**
   * Shows an error message when saving fails
   * @param error The error that occurred
   */
  private async showErrorMessage(error: unknown) {
    await Swal.fire({
      title: 'Errore',
      text: error instanceof Error ? error.message : 'Si è verificato un errore durante il salvataggio delle modifiche',
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  /**
   * Handles form cancellation
   * Emits the cancel event
   */
  onCancel() {
    this.cancel.emit();
  }

  async deleteSingleQuestion(queryId: string) {

    const confirmed = await this.showConfirm('');
    if (confirmed) {
    this.http.get(`${environment.apiUrl}/dashboard/deleteQuestion`, {
      params: { queryId }
    }).subscribe({
      next: () => {
        window.location.href = '/admin/dashboard';
      },
      error: (err) => {
        console.error('Errore durante l\'eliminazione della domanda:', err);
        alert('Errore durante l\'eliminazione');
      }
    });

    }
  }

  /**
   * Gets the label for a question type
   * @param type The question type
   * @returns The label in Italian
   */
  getQuestionTypeLabel(type: string | null): string {
    //if (!type || type === '21') return '';
    return QUESTION_TYPE_LABELS[type as keyof typeof QUESTION_TYPE_LABELS] || '';
  }
}
